import { createContext, type ReactNode, useCallback, useContext, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Toast, type ToastAction, type ToastVariant } from "./Toast";

const AUTO_DISMISS_VARIANTS = new Set<ToastVariant>(["success", "info"]);
const AUTO_DISMISS_DURATION_MS = 8000;

interface ToastOptions {
  description?: string;
  action?: ToastAction;
}

interface ToastEntry extends ToastOptions {
  id: number;
  variant: ToastVariant;
  title: string;
}

interface ToastContextValue {
  success: (title: string, options?: ToastOptions) => void;
  info: (title: string, options?: ToastOptions) => void;
  warning: (title: string, options?: ToastOptions) => void;
  error: (title: string, options?: ToastOptions) => void;
}

const ToastContext = createContext<ToastContextValue | null>(null);

export function useToast(): ToastContextValue {
  const context = useContext(ToastContext);
  if (!context) throw new Error("useToast must be used within a ToastProvider");
  return context;
}

export function ToastProvider({ children }: Readonly<{ children: ReactNode }>) {
  const [toasts, setToasts] = useState<ToastEntry[]>([]);
  const nextId = useRef(0);
  const timers = useRef(new Map<number, ReturnType<typeof setTimeout>>());

  const dismiss = useCallback((id: number) => {
    const timer = timers.current.get(id);
    if (timer) {
      clearTimeout(timer);
      timers.current.delete(id);
    }
    setToasts((current) => current.filter((toast) => toast.id !== id));
  }, []);

  const addToast = useCallback(
    (variant: ToastVariant, title: string, options?: ToastOptions) => {
      const id = nextId.current++;
      setToasts((current) => [...current, { id, variant, title, ...options }]);
      if (AUTO_DISMISS_VARIANTS.has(variant)) {
        timers.current.set(
          id,
          setTimeout(() => dismiss(id), AUTO_DISMISS_DURATION_MS),
        );
      }
    },
    [dismiss],
  );

  const value: ToastContextValue = {
    success: (title, options) => addToast("success", title, options),
    info: (title, options) => addToast("info", title, options),
    warning: (title, options) => addToast("warning", title, options),
    error: (title, options) => addToast("error", title, options),
  };

  return (
    <ToastContext.Provider value={value}>
      {children}
      {createPortal(
        <div className="pointer-events-none fixed bottom-4 left-4 z-[120] flex w-80 flex-col gap-2">
          {toasts.map((toast) => (
            <div key={toast.id} className="pointer-events-auto">
              <Toast
                variant={toast.variant}
                title={toast.title}
                description={toast.description}
                action={toast.action}
                onDismiss={() => dismiss(toast.id)}
              />
            </div>
          ))}
        </div>,
        document.body,
      )}
    </ToastContext.Provider>
  );
}
